import { z } from "zod";
import { COMPLETION_ITEMS, type CompletionItem } from "./completion-picker";
import {
  doctorNoticeBodySchema,
  doctorNoticeUnconfirmedClaims,
  type DoctorNoticeClaim,
} from "./doctor-notice";

// TES-1289 (S2 of TES-1267 §3) — "Sammanfattning efter vårdmötet".
//
// After the meeting the server creates a prefilled, UNPUBLISHED draft
// (MeetingSummaryDraft) that the doctor edits in step 4 "Recept och patienttexter",
// previews in step 6 and publishes himself. Published, it is a
// `doctor_notice` on the Meeting, the one free-standing care note under
// "Vårdanteckningar/Vårdhistorik" (TES-1267 §10). It is one of the three
// patient free-text sources (§2); "Läkarens kommentar till
// behandlingsbeslutet" (TES-1290) is another, and is kept apart.
//
// Her rules, which this file is the one place for:
//   - Prefilled from VERIFIED structured data only: the outcome, the chosen
//     completions, the planned medication, and the remiss only after its
//     Webdoc tick (`buildMeetingSummaryPrefill`).
//   - A-02's base copy, verbatim, conditional per sentence.
//   - With a final positive decision: no "kan vara lämplig", "planerar",
//     "preliminär", "villkorad" or "avvaktar", and no medication fields
//     (TES-1311, 09C 2026-10-13).
//   - Source changes after a manual edit never overwrite the doctor's text
//     (`meetingSummaryUpdate`): she chooses "Uppdatera utkastet" or "Behåll
//     min text".
//
// The prefill text itself is the draft's source fingerprint: the draft
// stores the prefill its body was based on (`basisPrefill`), and a source
// change is exactly a new prefill that differs from it. A change that does
// not change the text (an item of an outcome that lists none) is no change.

// A-02's headings, verbatim. TES-1400 (Ö-528, Emelie 2026-10-15):
//   "Mellanrubriker som Det här behöver du göra och Det här gör Ella visas endast när de tillför faktisk information."
// The summary is published followed by the generated "Vad händer nu?"
// (TES-1317), so a subheading whose only line says what that block already
// says is not written (see meetingSummaryPrefillSections); an empty one is
// dropped at publish (finalizeMeetingSummary).
export const MEETING_SUMMARY_HEADINGS = [
  "Sammanfattning efter mötet",
  "Det här behöver du göra",
  "Det här gör jag/Ella",
] as const;
export type MeetingSummaryHeading = (typeof MEETING_SUMMARY_HEADINGS)[number];

// The doctor's field, A-02's name for it.
export const MEETING_SUMMARY_FIELD_LABEL = "Sammanfattning efter vårdmötet";

export const meetingSummaryBodySchema = doctorNoticeBodySchema;

// What the prefill is built from. Every part is a fact the doctor has
// recorded or confirmed, never free text the server guesses at.
export interface MeetingSummarySources {
  // The meeting's outcome; null while it is not chosen (the session ended,
  // nothing documented yet).
  outcome: "proceed" | "completion" | "cannot_proceed" | null;
  // The treatment decision was published with the meeting ("Publicera
  // behandlingsbeslutet nu", TES-1185): the final positive decision.
  approved: boolean;
  // The chosen completions, on a completion.
  items: readonly CompletionItem[];
  // "Annan specificerad komplettering": the row's title, as she reads it.
  otherHeading?: string | null;
  // The planned medication (PlannedMedicationVersion / the meeting's
  // order). Null when there is none.
  plannedMedication: {
    medication: string | null;
    dose: string | null;
    frequency: string | null;
  } | null;
  // "Provbeställning/remiss för blodprov är skapad i Webdoc" is ticked.
  labOrderConfirmed: boolean;
}

// A-02 "AUTOMATISKT FÖRIFYLLDA UTKAST", verbatim.
const LINE = {
  thanks: "Tack för vårt samtal idag.",
  mayBeSuitable:
    "Utifrån det vi har gått igenom bedömer jag att medicinsk behandling kan vara lämplig för dig.",
  findTasks: "Du hittar uppgifterna och instruktionerna i appen.",
  labOrder: "Jag har skapat den elektroniska remissen för ditt blodprov.",
  // Ours (listed in the PR): A-02's sentence without "kan vara", for the
  // final positive decision (TES-1311: "Formuleringar om preliminär,
  // planerad och villkorad behandling … ska tas bort automatiskt").
  suitable:
    "Utifrån det vi har gått igenom bedömer jag att medicinsk behandling är lämplig för dig.",
} as const;

// A-02's placeholder "{begärda kompletteringar}": what she is asked to do, as the object of
// "behöver du …". Ours (listed in the PR); A-02 gives only the placeholder.
export const MEETING_SUMMARY_TASK_PHRASE: Record<
  Exclude<CompletionItem, "other">,
  string
> = {
  blood_pressure_pulse: "mäta ditt blodtryck och din vilopuls",
  previous_lab_results: "ladda upp dina tidigare provsvar",
  new_blood_test: "lämna ett blodprov",
  prescription_evidence: "ladda upp underlag om din nuvarande behandling",
};

// "Lämna blodprov" → "lämna blodprov"; an acronym ("EKG …") stays as it is.
function asObject(heading: string): string {
  const h = heading.trim();
  if (h.length > 1 && h[1] === h[1]!.toLowerCase()) {
    return h[0]!.toLowerCase() + h.slice(1);
  }
  return h;
}

function joinSv(parts: string[]): string {
  if (parts.length <= 1) return parts.join("");
  return `${parts.slice(0, -1).join(", ")} och ${parts[parts.length - 1]}`;
}

function taskPhrases(s: MeetingSummarySources): string[] {
  const out: string[] = [];
  // In the picker's order, whatever order the caller sends.
  for (const item of COMPLETION_ITEMS) {
    if (!s.items.includes(item)) continue;
    if (item === "other") {
      if (s.otherHeading?.trim()) out.push(asObject(s.otherHeading));
      continue;
    }
    // "Patienten får inte få uppgiften att lämna blodprov innan denna
    // bekräftelse finns" (A-02).
    if (item === "new_blood_test" && !s.labOrderConfirmed) continue;
    out.push(MEETING_SUMMARY_TASK_PHRASE[item]);
  }
  return out;
}

// "Jag planerar behandling med {läkemedel} {dos} {intervall}." Only with a
// planned medication; a part that is not written is left out.
function planSentence(s: MeetingSummarySources): string | null {
  const pm = s.plannedMedication;
  const medication = pm?.medication?.trim();
  if (!pm || !medication) return null;
  const parts = [medication, pm.dose?.trim(), pm.frequency?.trim()].filter(
    (p): p is string => Boolean(p),
  );
  return `Jag planerar behandling med ${parts.join(" ")}.`;
}

/** The summary's sections, each heading with its prefilled lines. */
export function meetingSummaryPrefillSections(
  s: MeetingSummarySources,
): Array<{ heading: MeetingSummaryHeading; lines: string[] }> {
  const summary: string[] = [LINE.thanks];
  const tasks: string[] = [];
  const mine: string[] = [];

  if (s.outcome === "proceed") {
    // TES-1311 approved variant: a decision taken, no medication fields.
    summary.push(s.approved ? LINE.suitable : LINE.mayBeSuitable);
  } else if (s.outcome === "completion") {
    // The plan sentence is the komplettering variant's only (TES-1311).
    summary.push(
      [LINE.mayBeSuitable, planSentence(s)].filter(Boolean).join(" "),
    );
    const phrases = taskPhrases(s);
    if (phrases.length > 0) {
      tasks.push(
        `Innan jag kan slutföra mitt behandlingsbeslut behöver du ${joinSv(phrases)}. ${LINE.findTasks}`,
      );
    }
    // TES-1400 (Ö-528) — A-02's "När kompletteringarna har kommit in går
    // jag igenom dom och återkommer med mitt beslut under Din vård." is no
    // longer written here: "Vad händer nu?" right after it always says the
    // same (WHAT_HAPPENS_NEXT_LINE.afterCompletion / .afterBloodTest), so
    // "Det här gör jag/Ella" appears only when the remiss is confirmed.
    if (s.items.includes("new_blood_test") && s.labOrderConfirmed) {
      mine.push(LINE.labOrder);
    }
  }
  // "Kan inte gå vidare" (Ö-529: the patient notice alone) and an outcome
  // not yet chosen: the thanks alone. The admin does not offer the summary
  // for the former.

  return [
    { heading: "Sammanfattning efter mötet" as const, lines: summary },
    { heading: "Det här behöver du göra" as const, lines: tasks },
    { heading: "Det här gör jag/Ella" as const, lines: mine },
  ].filter((section) => section.lines.length > 0);
}

/** The prefilled draft text, and the draft's source fingerprint. */
export function buildMeetingSummaryPrefill(s: MeetingSummarySources): string {
  return meetingSummaryPrefillSections(s)
    .map((section) => [section.heading, ...section.lines].join("\n"))
    .join("\n\n");
}

const HEADING_SET = new Set<string>(MEETING_SUMMARY_HEADINGS);

/**
 * The text as it is published: every heading with nothing under it is left
 * out, surrounding blank lines are trimmed, and runs of blank lines collapse
 * to one. An empty result means there is nothing to publish.
 */
export function finalizeMeetingSummary(body: string): string {
  const lines = body.replace(/\r\n?/g, "\n").split("\n");
  const blocks: string[][] = [[]];
  for (const line of lines) {
    if (HEADING_SET.has(line.trim())) blocks.push([line.trim()]);
    else blocks[blocks.length - 1]!.push(line);
  }
  return blocks
    .map((block) => {
      const [first, ...rest] = block;
      const isHeading = first !== undefined && HEADING_SET.has(first);
      const content = (isHeading ? rest : block).join("\n").trim();
      if (!content) return "";
      return isHeading ? `${first}\n${content}` : content;
    })
    .filter(Boolean)
    .join("\n\n")
    .replace(/\n{3,}/g, "\n\n");
}

/** What the summary claims before its Webdoc confirmation, if anything. */
export function meetingSummaryUnconfirmedClaims(
  body: string,
  labOrderConfirmed: boolean,
): DoctorNoticeClaim[] {
  // A meeting never confirms a prescription.
  return doctorNoticeUnconfirmedClaims(body, {
    labOrder: labOrderConfirmed,
    prescription: false,
  });
}

// --- the draft ---------------------------------------------------------------

export interface MeetingSummaryDraftState {
  body: string;
  basisPrefill: string;
  editedByDoctorAt: string | null;
}

/**
 * What the doctor sees for a draft, given the prefill the sources give now.
 *   - No draft, or a draft she has not edited: the current prefill. It
 *     follows the sources and is nothing she wrote.
 *   - An edited draft: her text, always. When the sources have changed since
 *     it was based on them, the new prefill is offered as a proposal
 *     ("Granska föreslagen uppdatering") and never applied without her.
 */
export function meetingSummaryUpdate(
  draft: MeetingSummaryDraftState | null,
  prefill: string,
): { text: string; edited: boolean; proposal: string | null } {
  if (!draft || draft.editedByDoctorAt === null) {
    return { text: prefill, edited: false, proposal: null };
  }
  return {
    text: draft.body,
    edited: true,
    proposal: draft.basisPrefill === prefill ? null : prefill,
  };
}

// "Förifyllt utkast – inte publicerat" is the ticket's (TES-1289). The other
// two are ours (listed in the PR).
export const MEETING_SUMMARY_STATUS_LABEL = {
  prefilled: "Förifyllt utkast – inte publicerat",
  edited: "Utkast – inte publicerat",
  published: "Publicerad under Din vård",
} as const;
export const MEETING_SUMMARY_PROPOSAL_COPY = {
  // The ticket's, verbatim.
  review: "Granska föreslagen uppdatering",
  apply: "Uppdatera utkastet",
  keep: "Behåll min text",
  // Ours.
  explain:
    "Underlaget har ändrats sedan du redigerade texten. Din text ändras inte förrän du väljer att uppdatera utkastet.",
} as const;

// PUT …/meetings/:meetingId/summary — the doctor's text and the prefill it
// is based on. "Uppdatera utkastet" sends the proposal as both; "Behåll min
// text" sends her text with the proposal as its new basis. A body equal to
// its basis is an unedited draft again.
export const saveMeetingSummarySchema = z.object({
  body: meetingSummaryBodySchema,
  basisPrefill: z.string().max(8000),
});
export type SaveMeetingSummaryRequest = z.infer<
  typeof saveMeetingSummarySchema
>;

// POST …/meetings/:meetingId/summary/publish — the text exactly as the
// doctor previewed it (before the empty headings are dropped). The server
// stores it as the draft and publishes its finalized form, so what she saw
// is what she gets. The publish refuses a remiss claim that the meeting has
// not confirmed (the same guard as every notice).
export const publishMeetingSummarySchema = z.object({
  body: meetingSummaryBodySchema,
  basisPrefill: z.string().max(8000),
});
export type PublishMeetingSummaryRequest = z.infer<
  typeof publishMeetingSummarySchema
>;

export const MEETING_SUMMARY_STATUSES = ["draft", "published"] as const;
export type MeetingSummaryStatus = (typeof MEETING_SUMMARY_STATUSES)[number];

export const meetingSummaryViewSchema = z.object({
  // Null when no draft exists yet (the session has not ended and nothing is
  // documented); the admin then shows the prefill it computes itself.
  draft: z
    .object({
      id: z.string(),
      // "published" while the published version is the current text; any
      // change after it makes it a draft again (a republish is version n+1).
      status: z.enum(MEETING_SUMMARY_STATUSES),
      body: z.string(),
      basisPrefill: z.string(),
      editedByDoctorAt: z.string().nullable(),
      // The treatment decision was published with the meeting: the prefill
      // is the approved variant.
      decisionApproved: z.boolean(),
      published: z
        .object({
          noticeId: z.string(),
          version: z.number().int(),
          publishedAt: z.string(),
          publishedByEmployeeId: z.string(),
          // TES-1686 — the published notice's text, exactly as the patient
          // reads it under Din vård: the summary followed by the generated
          // "Vad händer nu?". Can differ from `body` once the doctor edits
          // after the publish.
          body: z.string(),
        })
        .nullable(),
    })
    .nullable(),
  // The prefill the server's stored sources give now. Equal to the admin's
  // own once the meeting is documented.
  prefill: z.string(),
});
export type MeetingSummaryView = z.infer<typeof meetingSummaryViewSchema>;

export const MEETING_SUMMARY_EMPTY = "meeting_summary_empty";
